import { useApiQuery } from '@/api/main/query'
import { PaginatedCollection } from '@/api/types'
import { deviceAPI } from '@/features/devices/api/endpoints'
import { useRedirectToApk } from '@/features/devices/hooks/useRedirectToApk'
import { useUpdateDeviceName } from '@/features/devices/hooks/useUpdateDeviceName'
import { getDevicesColumns } from '@/features/devices/table/columns'
import { DeviceModel } from '@/features/devices/types/types'
import { DevicesHeader } from '@/features/devices/ui/DevicesHeader/DevicesHeader'
import { DevicesTabs } from '@/features/devices/ui/DevicesTabs/DevicesTabs'
import { AddNewDevice } from '@/features/devices/ui/dialogs/AddNewDevice'
import { ConnectNewDevice } from '@/features/devices/ui/dialogs/ConnectNewDevice'
import { RenameDevice } from '@/features/devices/ui/dialogs/RenameDevice'
import { MobileBlock } from '@/features/devices/ui/MobileBlockDevice/MobileBlock'
import { DEFAULT_TAKE } from '@/shared/constants/constants'
import { useDebounce, useToast } from '@/shared/hooks'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { BaseTable, BaseTableSkeleton } from 'syncra-storybook/features'

export const Devices = () => {
  const { addToast } = useToast()
  const queryClient = useQueryClient()

  const [search, setSearch] = useState('')
  const [skip, setSkip] = useState(0)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [selectedDeviceStatus, setSelectedDeviceStatus] = useState(0)

  const [isAddDeviceModalOpen, setIsAddDeviceModalOpen] = useState(false)
  const [isConnectDeviceModalOpen, setIsConnectDeviceModalOpen] = useState(false)
  const [isRenameDeviceModalOpen, setIsRenameDeviceModalOpen] = useState(false)
  console.log(isRenameDeviceModalOpen)

  const [deviceToConnect, setDeviceToConnect] = useState<{ key: string; qrCode: string } | null>(
    null
  )
  const [deviceIdToRename, setDeviceIdToRename] = useState<string>('')

  const debouncedSearch = useDebounce(search, 500)
  const debouncedSkip = useDebounce(skip, 500)
  const effectiveSkip = skip === 0 ? 0 : debouncedSkip

  const toggleRowSelection = (id: string) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  const { redirectToApk } = useRedirectToApk()

  const {
    data: queryDevicesData,
    error: devicesError,
    isLoading: devicesIsLoad,
  } = useApiQuery<PaginatedCollection<DeviceModel[]>>(
    ['devices', effectiveSkip, DEFAULT_TAKE, debouncedSearch, selectedDeviceStatus],
    deviceAPI.baseKey,
    {
      skip: effectiveSkip,
      take: DEFAULT_TAKE,
      textFilter: debouncedSearch,
      status: selectedDeviceStatus,
    }
  )

  const {
    data: deviceToRename,
    error: deviceError,
    refetch: refetchDeviceData,
  } = useApiQuery<DeviceModel>(
    ['device', deviceIdToRename],
    deviceAPI.getDevice(deviceIdToRename),
    undefined,
    {
      enabled: Boolean(deviceIdToRename),
    }
  )

  const handleOpenConnectModal = (key: string, qrCode: string) => {
    setDeviceToConnect({ key, qrCode })
    setIsConnectDeviceModalOpen(true)
  }

  const handleOpenRenameModal = (id: string) => {
    setDeviceIdToRename(id)
    refetchDeviceData()
  }

  const { handleUpdateName } = useUpdateDeviceName(deviceIdToRename, () => {
    setIsRenameDeviceModalOpen(false)
    setDeviceIdToRename('')
  })

  useEffect(() => {
    if (deviceError) {
      addToast('Ошибка!', deviceError.message, 'error')
    }
  }, [deviceError, addToast])

  useEffect(() => {
    if (deviceIdToRename) {
      setIsRenameDeviceModalOpen(true)
    }
  }, [deviceIdToRename])

  const handleCloseRenameModal = () => {
    setIsRenameDeviceModalOpen(false)
    setDeviceIdToRename('')
  }

  const handleCloseConnectModal = () => {
    setIsConnectDeviceModalOpen(false)
    setDeviceToConnect(null)
    queryClient.invalidateQueries({ queryKey: ['devices'] })
  }

  return (
    <div className="flex flex-col gap-4 w-full">
      <DevicesHeader setModalAddDevice={setIsAddDeviceModalOpen} onDownloadClick={redirectToApk} />
      <DevicesTabs
        setSearch={setSearch}
        selectedIndex={selectedDeviceStatus}
        selectFilterBtn={(index) => {
          setSelectedDeviceStatus(index)
          setSkip(0)
        }}
      />

      {devicesError ? (
        <p>Ошибка загрузки данных.</p>
      ) : devicesIsLoad ? (
        <BaseTableSkeleton />
      ) : !queryDevicesData?.items || queryDevicesData.items.length === 0 ? (
        <p>Устройств нет.</p>
      ) : (
        <BaseTable
          columns={getDevicesColumns({
            connectNewDevice: handleOpenConnectModal,
            onEdit: handleOpenRenameModal,
          })}
          data={queryDevicesData.items}
          totalItems={queryDevicesData?.count}
          skip={skip}
          onPageChange={(newSkip) => setSkip(newSkip)}
          cardClasses="border-0"
          mobileCards={queryDevicesData.items.map((detail) => (
            <MobileBlock
              key={detail.id}
              device={detail}
              isSelected={selectedIds.has(detail.id)}
              onSelect={() => toggleRowSelection(detail.id)}
              onEdit={handleOpenRenameModal}
              connectNewDevice={handleOpenConnectModal}
            />
          ))}
          isRowLink
        />
      )}

      {/* Модальные окна */}
      <AddNewDevice modalVisible={isAddDeviceModalOpen} setModalVisible={setIsAddDeviceModalOpen} />

      {deviceIdToRename && deviceToRename && (
        <RenameDevice
          deviceData={deviceToRename}
          modalVisible={isRenameDeviceModalOpen}
          setModalVisible={handleCloseRenameModal}
          onSubmit={handleUpdateName}
        />
      )}

      {deviceToConnect && (
        <ConnectNewDevice
          modalVisible={isConnectDeviceModalOpen}
          setModalVisible={handleCloseConnectModal}
          device={deviceToConnect}
        />
      )}
    </div>
  )
}
